import { api } from './apiClient';

export interface UploadUrlResponse {
  status: number;
  data: string;
  message: string;
}

export interface UploadUrlBatchRequest {
  fileTypeEnum: 'IMAGE' | 'FILE';
  urlList: string[];
}

export interface UploadUrlBatchResponse {
  status: number;
  data: string;
  message: string;
}

export interface CommonAttachFile {
  id: number;
  caseNo: string;
  fileType: string;
  fileSize?: number;
  fullUrl: string;
  fileText?: string;
  fileIntro?: string;
  uuid?: number;
  gmtCreate: string;
  gmtModified: string;
  isExist: number;
  originalFileName?: string;
  createUserName?: string;
}

/** 单文件上传大小上限（与后端 application.yml multipart 配置一致） */
export const MAX_UPLOAD_FILE_SIZE_MB = 500;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

export const commonAttachFileService = {
  // 单文件上传获取URL
  uploadUrl: async (file: File): Promise<UploadUrlResponse> => {
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      throw new Error(`文件大小不能超过 ${MAX_UPLOAD_FILE_SIZE_MB}MB`);
    }

    const formData = new FormData();
    formData.append('file', file);
    
    try {
      return await api.post('/admin/common/upload/url', formData, {
        timeout: 600000,
        maxContentLength: MAX_UPLOAD_FILE_SIZE_BYTES,
        maxBodyLength: MAX_UPLOAD_FILE_SIZE_BYTES,
        headers: {
          // 不设置Content-Type，让axios自动设置multipart/form-data和boundary
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          console.log(`上传进度: ${percentCompleted}%`);
        },
      });
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('上传超时，请检查网络连接或文件大小');
      } else if (error.response?.status === 413) {
        throw new Error(`文件过大，请选择小于${MAX_UPLOAD_FILE_SIZE_MB}MB的文件`);
      } else if (error.message?.includes('EOFException')) {
        throw new Error('文件上传中断，请重试');
      }
      throw error;
    }
  },

  // 批量URL上传获取caseNo
  uploadUrlBatch: async (data: UploadUrlBatchRequest): Promise<UploadUrlBatchResponse> => {
    try {
      return await api.post('/admin/common/upload/url/batch/caseNo', data, {
        timeout: 120000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('请求超时，请重试');
      }
      throw error;
    }
  },

  // 根据caseNo查询附件列表
  listByCaseNo: async (caseNo: string): Promise<{ status: number; data: CommonAttachFile[]; message: string }> => {
    try {
      return await api.get('/admin/common/attach/listByCaseNo', {
        params: {
          caseNo,
        },
      });
    } catch (error: any) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('请求超时，请重试');
      }
      throw error;
    }
  },
};
